import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  CustomisationService,
  type CustomisationSettings,
  type CustomisationItem,
} from '../lib/customisation/customisationService';
import { useAuthStore } from './authStore';
import { getAvatarUrl } from '../assets/avatar';
import type { Avatar } from '../constants/avatars';

interface CustomisationState {
  settings: CustomisationSettings;
  isLoading: boolean;
  error: string | null;
  lastSyncedSettings: CustomisationSettings;
  hasUnsavedChanges: boolean;

  // User custom avatars
  userAvatars: Avatar[];

  // Mobile preview state
  isModifyingWidget: boolean;
  setIsModifyingWidget: (value: boolean) => void;

  // Abandoned cart settings
  abandonedCartSettings: {
    abandonEnable: string;
    abandon1: string;
    abandon2: string;
    abandon3: string;
  };

  // Welcome settings
  welcomeSettings: {
    welcomeEnable: string;
    welcomeMessage: string;
    welcomeDevice: string;
    welcomeDuration: string;
  };

  // Multilingual settings
  multilingualSettings: {
    multilingualEnable: string;
    language: string;
  };

  // Local state management
  updateSettings: (settings: Partial<CustomisationSettings>) => void;
  resetSettings: () => void;

  // API operations
  loadSettings: () => Promise<void>;
  loadUserAvatars: () => Promise<void>;
  saveSettings: () => Promise<void>;
  resetToDefault: () => Promise<void>;
  
  // Abandoned cart operations
  loadAbandonedCartSettings: () => Promise<void>;
  saveAbandonedCartSettings: (settings: Partial<CustomisationState['abandonedCartSettings']>) => Promise<void>;

  // Welcome operations
  loadWelcomeSettings: () => Promise<void>;
  saveWelcomeSettings: (settings: Partial<CustomisationState['welcomeSettings']>) => Promise<void>;

  // Multilingual operations
  loadMultilingualSettings: () => Promise<void>;
  saveMultilingualSettings: (settings: Partial<CustomisationState['multilingualSettings']>) => Promise<void>;

  // Utility methods
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  markAsSynced: () => void;
  reset: () => void;
}

const defaultSettings: CustomisationSettings = {
  agentName: 'YepAI',
  avatarName: 'Bella',
  welcomeMessage: 'Hey there, how can I help you?',
  responseLength: '100',
  widgetPosition: 'right',
  widgetHandling: 'closed',
  primaryColor: '#FFE5D9',
  glassLevel: '0.5',
  companyLogo: '',
  enableLead: 'active',
  leadEmailNotification: 'no',
  leadEmailRecipient: '',
  leadGenerationSensitivity: '0.3',
  leadOnStart: 'yes',
  meetingLink: '',
  referrerUrl: '',
  subheading: 'Our bot answers instantly',
  supportEmail: '',
  widgetButton: '{"launcher":"avatar","icon":"avatar","size":"small","label":"Chat with me"}',
  // PRD #18 Quick Reply Chips: empty means the widget falls back to whatever it ships with.
  quickReplyChips: '[]',
  // PRD #18 Custom launcher. Empty = the merchant never uploaded one.
  launcherImage: '',
  widgetStatus: 'closed',
  paymentStatus: 'startup',
};

// Helper function to get only changed fields
const getChangedFields = (
  currentSettings: CustomisationSettings,
  originalSettings: CustomisationSettings
): Partial<CustomisationSettings> => {
  const changedFields: Partial<CustomisationSettings> = {};

  Object.keys(currentSettings).forEach(key => {
    const typedKey = key as keyof CustomisationSettings;
    if (currentSettings[typedKey] !== originalSettings[typedKey]) {
      (changedFields as any)[typedKey] = currentSettings[typedKey];
    }
  });

  return changedFields;
};

const defaultAbandonedCartSettings = {
  abandonEnable: 'off',
  abandon1: 'Order is almost complete! Can you tell us why you abandoned the cart, please?',
  abandon2: 'What\'s causing that at the moment?',
  abandon3: '',
};

const defaultWelcomeSettings = {
  welcomeEnable: 'off',
  welcomeMessage: 'Hi there 👋 If you need any assistance, I\'m always here.',
  welcomeDevice: 'desktop', // all, desktop, mobile
  welcomeDuration: '5', // seconds
};

const defaultMultilingualSettings = {
  multilingualEnable: 'off',
  language: 'en',
};

export const useCustomisationStore = create<CustomisationState>()(
  persist(
    (set, get) => ({
      settings: defaultSettings,
      isLoading: false,
      error: null,
      lastSyncedSettings: defaultSettings,
      hasUnsavedChanges: false,
      userAvatars: [],
      isModifyingWidget: false,
      setIsModifyingWidget: (value) => set({ isModifyingWidget: value }),
      abandonedCartSettings: defaultAbandonedCartSettings,
      welcomeSettings: defaultWelcomeSettings,
      multilingualSettings: defaultMultilingualSettings,

      // Local state management
      updateSettings: newSettings => {
        set(state => {
          const updatedSettings = { ...state.settings, ...newSettings };
          return {
            settings: updatedSettings,
            hasUnsavedChanges:
              JSON.stringify(updatedSettings) !== JSON.stringify(state.lastSyncedSettings),
            error: null,
          };
        });
      },

      resetSettings: () => {
        set(state => ({
          settings: state.lastSyncedSettings,
          hasUnsavedChanges: false,
          error: null,
        }));
      },

      // API operations
      loadSettings: async () => {
        const { user } = useAuthStore.getState();
        if (!user?.id) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });

        // Load user avatars in parallel (non-blocking)
        get().loadUserAvatars();

        try {
          const response = await CustomisationService.getCustomisations(user.id);
          const loadedSettings = response.settings || defaultSettings;

          set({
            settings: loadedSettings,
            lastSyncedSettings: loadedSettings,
            hasUnsavedChanges: false,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error('Failed to load customisation settings:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load settings',
          });
        }
      },

      loadUserAvatars: async () => {
        const { user } = useAuthStore.getState();
        if (!user?.id) return;

        try {
          const avatars = await CustomisationService.getUserAvatars(user.id);
          const mapped: Avatar[] = avatars.map(item => {
            const name = item.avatarUrl;
            return {
              value: name,
              img: getAvatarUrl(name),
              category: 'others',
              isCustom: true,
            };
          });
          set({ userAvatars: mapped });
        } catch (error) {
          console.error('Failed to load user avatars:', error);
        }
      },

      saveSettings: async () => {
        const { user } = useAuthStore.getState();
        const { settings, lastSyncedSettings } = get();

        if (!user?.id) {
          set({ error: 'User not authenticated' });
          return;
        }

        // Only send changed fields
        const changedFields = getChangedFields(settings, lastSyncedSettings);
        if (Object.keys(changedFields).length === 0) {
          console.log('No changes detected, skipping save');
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const response = await CustomisationService.updateCustomisations(user.id, changedFields);
          const savedSettings = response.settings;

          set({
            settings: savedSettings,
            lastSyncedSettings: savedSettings,
            hasUnsavedChanges: false,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error('Failed to save customisation settings:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to save settings',
          });
          throw error; // Re-throw to allow component to handle
        }
      },

      resetToDefault: async () => {
        const { user } = useAuthStore.getState();
        if (!user?.id) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const response = await CustomisationService.resetCustomisations(user.id);
          const resetSettings = response.settings || defaultSettings;

          set({
            settings: resetSettings,
            lastSyncedSettings: resetSettings,
            hasUnsavedChanges: false,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error('Failed to reset customisation settings:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to reset settings',
          });
        }
      },

      // Abandoned cart operations
      loadAbandonedCartSettings: async () => {
        const { user } = useAuthStore.getState();
        if (!user?.id) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // Fetch abandoned cart settings with subCategory parameter
          const abandonItems = await CustomisationService.getCustomisationsWithParams(user.id, {
            subCategory: 'abandon'
          });
          
          const abandonedSettings = { ...defaultAbandonedCartSettings };
          
          // Map each key to the corresponding field
          abandonItems.forEach((item: any) => {
            switch(item.key) {
              case 'abandonEnable':
                abandonedSettings.abandonEnable = item.value;
                break;
              case 'abandonMessage':
                abandonedSettings.abandon1 = item.value;
                break;
              case 'abandonFollow':
                abandonedSettings.abandon2 = item.value;
                break;
              case 'abandonCode':
                abandonedSettings.abandon3 = item.value;
                break;
            }
          });
          
          set({
            abandonedCartSettings: abandonedSettings,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error('Failed to load abandoned cart settings:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load abandoned cart settings',
          });
        }
      },

      saveAbandonedCartSettings: async (settings) => {
        const { user } = useAuthStore.getState();
        if (!user?.id) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // Update local state first
          const currentSettings = get().abandonedCartSettings;
          const updatedSettings = {
            ...currentSettings,
            ...settings
          };
          
          set({
            abandonedCartSettings: updatedSettings
          });
          
          // Create payload with only the changed fields
          const payload: CustomisationItem[] = [];
          
          // Only add fields that were actually provided in the settings parameter
          if ('abandonEnable' in settings) {
            payload.push({
              key: 'abandonEnable',
              value: updatedSettings.abandonEnable,
              type: 'chatbot' as const,
              subCategory: 'abandon',
              userId: user.id
            });
          }
          
          if ('abandon1' in settings) {
            payload.push({
              key: 'abandonMessage',
              value: updatedSettings.abandon1,
              type: 'chatbot' as const,
              subCategory: 'abandon',
              userId: user.id
            });
          }
          
          if ('abandon2' in settings) {
            payload.push({
              key: 'abandonFollow',
              value: updatedSettings.abandon2,
              type: 'chatbot' as const,
              subCategory: 'abandon',
              userId: user.id
            });
          }
          
          if ('abandon3' in settings) {
            payload.push({
              key: 'abandonCode',
              value: updatedSettings.abandon3,
              type: 'chatbot' as const,
              subCategory: 'abandon',
              userId: user.id
            });
          }
          
          await CustomisationService.updateCustomisationsRaw(user.id, payload);
          
          set({
            isLoading: false,
            error: null,
          });
          
          // Reload to ensure sync
          await get().loadAbandonedCartSettings();
        } catch (error) {
          console.error('Failed to save abandoned cart settings:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to save abandoned cart settings',
          });
          throw error;
        }
      },

      // Welcome operations
      loadWelcomeSettings: async () => {
        const { user } = useAuthStore.getState();
        if (!user?.id) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // Fetch welcome settings with subCategory parameter
          const welcomeItems = await CustomisationService.getCustomisationsWithParams(user.id, {
            subCategory: 'welcome'
          });
          
          const welcomeSettings = { ...defaultWelcomeSettings };
          
          // Map each key to the corresponding field
          welcomeItems.forEach((item: any) => {
            switch(item.key) {
              case 'welcomeEnable':
                welcomeSettings.welcomeEnable = item.value;
                break;
              case 'welcomeMessage':
                welcomeSettings.welcomeMessage = item.value;
                break;
              case 'welcomeDevice':
                welcomeSettings.welcomeDevice = item.value;
                break;
              case 'welcomeDuration':
                welcomeSettings.welcomeDuration = item.value;
                break;
            }
          });
          
          set({
            welcomeSettings: welcomeSettings,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error('Failed to load welcome settings:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load welcome settings',
          });
        }
      },

      saveWelcomeSettings: async (settings) => {
        const { user } = useAuthStore.getState();
        if (!user?.id) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // Update local state first
          const currentSettings = get().welcomeSettings;
          const updatedSettings = {
            ...currentSettings,
            ...settings
          };
          
          set({
            welcomeSettings: updatedSettings
          });
          
          // Create payload with only the changed fields
          const payload: CustomisationItem[] = [];
          
          // Only add fields that were actually provided in the settings parameter
          if ('welcomeEnable' in settings) {
            payload.push({
              key: 'welcomeEnable',
              value: updatedSettings.welcomeEnable,
              type: 'chatbot' as const,
              subCategory: 'welcome',
              userId: user.id
            });
          }
          
          if ('welcomeMessage' in settings) {
            payload.push({
              key: 'welcomeMessage',
              value: updatedSettings.welcomeMessage,
              type: 'chatbot' as const,
              subCategory: 'welcome',
              userId: user.id
            });
          }
          
          if ('welcomeDevice' in settings) {
            payload.push({
              key: 'welcomeDevice',
              value: updatedSettings.welcomeDevice,
              type: 'chatbot' as const,
              subCategory: 'welcome',
              userId: user.id
            });
          }
          
          if ('welcomeDuration' in settings) {
            payload.push({
              key: 'welcomeDuration',
              value: updatedSettings.welcomeDuration,
              type: 'chatbot' as const,
              subCategory: 'welcome',
              userId: user.id
            });
          }
          
          await CustomisationService.updateCustomisationsRaw(user.id, payload);
          
          set({
            isLoading: false,
            error: null,
          });
          
          // Reload to ensure sync
          await get().loadWelcomeSettings();
        } catch (error) {
          console.error('Failed to save welcome settings:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to save welcome settings',
          });
          throw error;
        }
      },

      // Multilingual operations
      loadMultilingualSettings: async () => {
        const { user } = useAuthStore.getState();
        if (!user?.id) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // Fetch multilingual settings with subCategory parameter
          const multilingualItems = await CustomisationService.getCustomisationsWithParams(user.id, {
            subCategory: 'language'
          });

          const multilingualSettings = { ...defaultMultilingualSettings };

          // Map each key to the corresponding field (filter by type=chatbot and subCategory=language)
          multilingualItems
            .filter((item: any) => item.type === 'chatbot' && item.subCategory === 'language')
            .forEach((item: any) => {
              switch(item.key) {
                case 'languageEnable':
                  multilingualSettings.multilingualEnable = item.value;
                  break;
                case 'language':
                  multilingualSettings.language = item.value;
                  break;
              }
            });

          set({
            multilingualSettings: multilingualSettings,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error('Failed to load multilingual settings:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load multilingual settings',
          });
        }
      },

      saveMultilingualSettings: async (settings) => {
        const { user } = useAuthStore.getState();
        if (!user?.id) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // Update local state first
          const currentSettings = get().multilingualSettings;
          const updatedSettings = {
            ...currentSettings,
            ...settings
          };

          set({
            multilingualSettings: updatedSettings
          });

          // Create payload with only the changed fields
          const payload: CustomisationItem[] = [];

          // Only add fields that were actually provided in the settings parameter
          if ('multilingualEnable' in settings) {
            payload.push({
              key: 'languageEnable',
              value: updatedSettings.multilingualEnable,
              type: 'chatbot' as const,
              subCategory: 'language',
              userId: user.id
            });
          }

          if ('language' in settings) {
            payload.push({
              key: 'language',
              value: updatedSettings.language,
              type: 'chatbot' as const,
              subCategory: 'language',
              userId: user.id
            });
          }

          await CustomisationService.updateCustomisationsRaw(user.id, payload);

          set({
            isLoading: false,
            error: null,
          });

          // Reload to ensure sync
          await get().loadMultilingualSettings();
        } catch (error) {
          console.error('Failed to save multilingual settings:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to save multilingual settings',
          });
          throw error;
        }
      },

      // Utility methods
      setLoading: loading => set({ isLoading: loading }),
      setError: error => set({ error }),
      markAsSynced: () => {
        const { settings } = get();
        set({
          lastSyncedSettings: settings,
          hasUnsavedChanges: false,
        });
      },

      reset: () => {
        set({
          settings: defaultSettings,
          lastSyncedSettings: defaultSettings,
          hasUnsavedChanges: false,
          isLoading: false,
          error: null,
          userAvatars: [],
          isModifyingWidget: false,
          abandonedCartSettings: defaultAbandonedCartSettings,
          welcomeSettings: defaultWelcomeSettings,
          multilingualSettings: defaultMultilingualSettings,
        });
      },
    }),
    {
      name: 'customisation-storage',
      partialize: state => ({
        settings: state.settings,
        lastSyncedSettings: state.lastSyncedSettings,
        multilingualSettings: state.multilingualSettings,
      }),
    }
  )
);
